import { SpellType } from '../game/CooldownManager';

export interface KeyboardSpellMap {
  key: string;
  spell: SpellType;
  label: string;
}

const SPELL_KEY_MAPPINGS: KeyboardSpellMap[] = [
  { key: 'q', spell: 'fireball', label: 'Q' },
  { key: '1', spell: 'fireball', label: 'Q' },
  { key: 'w', spell: 'iceShard', label: 'W' },
  { key: '2', spell: 'iceShard', label: 'W' },
  { key: 'e', spell: 'lightningBolt', label: 'E' },
  { key: '3', spell: 'lightningBolt', label: 'E' },
  { key: 'r', spell: 'shield', label: 'R' },
  { key: '4', spell: 'shield', label: 'R' },
  { key: 'f', spell: 'healSpell', label: 'F' },
  { key: '5', spell: 'healSpell', label: 'F' },
  { key: 't', spell: 'reflect', label: 'T' },
  { key: '6', spell: 'reflect', label: 'T' },
  { key: 'g', spell: 'teleport', label: 'G' },
  { key: '7', spell: 'teleport', label: 'G' },
];

/** Returns the primary key hint letter for a given spell type */
export function getKeyHint(spell: SpellType): string {
  const HINTS: Record<SpellType, string> = {
    fireball: 'Q',
    iceShard: 'W',
    lightningBolt: 'E',
    shield: 'R',
    healSpell: 'F',
    reflect: 'T',
    teleport: 'G',
    meteor: 'Z',
    superShield: 'X',
    blizzard: 'C',
  };
  return HINTS[spell];
}

const STORAGE_KEY = 'wizard-duel-keyboard-enabled';

/** Load keyboard preference from localStorage */
export function loadKeyboardPreference(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Default to true (keyboard enabled) if no preference saved
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

/** Save keyboard preference to localStorage */
export function saveKeyboardPreference(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

export class KeyboardInput {
  private onSpellCast: ((spell: SpellType) => void) | null = null;
  private enabled: boolean = false;
  private boundKeyDown: (e: KeyboardEvent) => void;

  constructor() {
    this.boundKeyDown = this.handleKeyDown.bind(this);
  }

  /** Start listening for keyboard spell inputs */
  enable(onSpellCast: (spell: SpellType) => void): void {
    if (this.enabled) {
      this.onSpellCast = onSpellCast;
      return;
    }
    this.onSpellCast = onSpellCast;
    this.enabled = true;
    window.addEventListener('keydown', this.boundKeyDown);
  }

  /** Stop listening for keyboard spell inputs */
  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    this.onSpellCast = null;
    window.removeEventListener('keydown', this.boundKeyDown);
  }

  private handleKeyDown(e: KeyboardEvent): void {
    // Don't intercept when modifier keys are held (browser shortcuts)
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    // Don't intercept if the user is typing in an input field
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const key = e.key.toLowerCase();
    const mapping = SPELL_KEY_MAPPINGS.find(m => m.key === key);

    if (mapping && this.onSpellCast) {
      e.preventDefault();
      this.onSpellCast(mapping.spell);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }
}
